import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

export type ThemeMode = "light" | "dark";

export const THEME_STORAGE_KEY = "emmi-bo-theme";

// localStorage can throw (Safari private mode, storage disabled by policy,
// quota exceeded). The theme must keep working in-memory in those cases.
const safeGetStoredTheme = (): ThemeMode | null => {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    return stored === "light" || stored === "dark" ? stored : null;
  } catch {
    return null;
  }
};

const safeStoreTheme = (theme: ThemeMode): void => {
  try {
    localStorage.setItem(THEME_STORAGE_KEY, theme);
  } catch {
    // Persistence failure is non-fatal: the theme still applies in-memory.
  }
};

// Stored preference wins; otherwise follow the OS preference. matchMedia is
// guarded because jsdom (tests) does not implement it.
const getInitialTheme = (): ThemeMode => {
  const stored = safeGetStoredTheme();
  if (stored) {
    return stored;
  }
  try {
    if (
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-color-scheme: dark)").matches
    ) {
      return "dark";
    }
  } catch {
    // Fall through to the default.
  }
  return "light";
};

interface ThemeContextValue {
  theme: ThemeMode;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [theme, setTheme] = useState<ThemeMode>(getInitialTheme);

  useEffect(() => {
    // data-theme on <html> drives the SCSS side ([data-theme="dark"] tokens);
    // antd's algorithm is driven separately via ConfigProvider.
    document.documentElement.dataset.theme = theme;
    safeStoreTheme(theme);
  }, [theme]);

  const toggleTheme = useCallback(() => {
    setTheme((prev) => (prev === "light" ? "dark" : "light"));
  }, []);

  const value = useMemo(() => ({ theme, toggleTheme }), [theme, toggleTheme]);

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
};

export const useTheme = (): ThemeContextValue => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};
